/**
 * One automatic reload when a lazily loaded chunk of an OLDER build is gone.
 *
 * A tab opened before a Hosting deploy still runs the old entry, and the old entry's
 * lazy chunks (the Video Grinder, the style guide, every lazy page) have hashed names
 * the new deploy no longer serves. Vite reports such a failed import as a
 * `vite:preloadError` event on `window`; the only cure is the new build, so the page
 * reloads - ONCE. A second failure soon after that reload is left to the error
 * boundaries (layout/RouteErrorBoundary.tsx), which offer "Reload" by hand: a build
 * that is really broken must never become a reload loop.
 *
 * THE GUARD IS sessionStorage, the one store a reload keeps and a new tab does not.
 * Storage that cannot be read or written (a locked-down browser) means no automatic
 * reload at all, for the same reason: without the guard a loop could not be ruled out.
 * The event is not cancelled, so the failed import still rejects and a boundary shows
 * its recovery screen for the moment the reload takes.
 */

/** Where the time of the last automatic reload is kept (epoch milliseconds). */
export const STALE_CHUNK_RELOAD_KEY = "loopgrowth:stale-chunk-reload-at";

/** A chunk failure within this long after an automatic reload is not reloaded again. */
export const STALE_CHUNK_RELOAD_GUARD_MS = 60_000;

/** The event Vite dispatches on `window` when a dynamic import's chunk cannot be loaded. */
export const VITE_PRELOAD_ERROR_EVENT = "vite:preloadError";

export interface StaleChunkReloadDeps {
    /** May throw: a browser can refuse storage outright. */
    readonly storage: () => Storage;
    readonly reload: () => void;
    /** Epoch milliseconds. */
    readonly now: () => number;
}

function defaultDeps(): StaleChunkReloadDeps {
    return {
        storage: () => window.sessionStorage,
        reload: () => { window.location.reload(); },
        now: () => Date.now()
    };
}

/**
 * Reloads the page unless an automatic reload already happened within the guard
 * window, or the guard cannot be kept. Returns whether it reloaded.
 */
export function reloadOnceForStaleChunk(overrides: Partial<StaleChunkReloadDeps> = {}): boolean {
    const deps: StaleChunkReloadDeps = { ...defaultDeps(), ...overrides };
    try {
        const storage = deps.storage();
        const now = deps.now();
        const last = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY) ?? Number.NaN);
        if (Number.isFinite(last) && now - last >= 0 && now - last < STALE_CHUNK_RELOAD_GUARD_MS) {
            return false;
        }
        storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
    } catch {
        return false;
    }
    deps.reload();
    return true;
}

/** Listens for Vite's chunk failures on `target` (the window). Returns the unsubscribe. */
export function installStaleChunkReload(
    overrides: Partial<StaleChunkReloadDeps> = {},
    target: EventTarget = window
): () => void {
    const onPreloadError = (): void => {
        reloadOnceForStaleChunk(overrides);
    };
    target.addEventListener(VITE_PRELOAD_ERROR_EVENT, onPreloadError);
    return () => { target.removeEventListener(VITE_PRELOAD_ERROR_EVENT, onPreloadError); };
}
